import { KnownMembership, MatrixClient, MatrixError, RoomType } from 'matrix-js-sdk/src/matrix';

import { getSpaceChildEvents } from 'components/BreakoutRooms/utils';
import { MatrixErrorEnum } from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/utils';
import logger from 'utils/logging/faro';
import { getJoinedMemberCount } from 'utils/matrixUtils';
import { hasWorkspaceMeta } from 'utils/roomHierarchyUtils';
import { sleep } from 'utils/utils';

export const leaveWithRateLimitRetry = async (matrixClient: MatrixClient, roomId: string) => {
  try {
    const room = matrixClient.getRoom(roomId);
    const isAlone = getJoinedMemberCount(room) === 1;
    const alias = room?.getCanonicalAlias();

    if (isAlone) {
      const invitees = room?.getMembersWithMembership(KnownMembership.Invite) ?? [];

      for (const member of invitees) {
        await matrixClient.kick(roomId, member.userId).catch((error) => logger.error('Error kicking member', error));
      }
      // NOTE: intentionally not doing anything (just logging the error) to avoid blocking the leave operation
      if (alias) await matrixClient.deleteAlias(alias).catch((error) => logger.error('Error deleting alias', error));
    }

    await matrixClient.leave(roomId);
    return;
  } catch (error) {
    if (!(error instanceof MatrixError) || error.errcode !== MatrixErrorEnum.M_LIMIT_EXCEEDED) throw error;

    const retryAfterMs = error.data?.retry_after_ms ?? 100;
    await sleep(retryAfterMs);
  }

  await matrixClient.leave(roomId);
};

const shouldSkipLeaving = (matrixClient: MatrixClient, roomId: string, shouldSkipWorkspaceRooms: boolean) => {
  const room = matrixClient.getRoom(roomId);
  const membership = room?.getMyMembership();
  const shouldSkipWorkspace = shouldSkipWorkspaceRooms && room ? hasWorkspaceMeta(room) : false;

  return !room || membership === KnownMembership.Leave || membership === KnownMembership.Ban || shouldSkipWorkspace;
};

//Collect the whole ordered tree of space sub-spaces and rooms within space and sub-spaces
export const collectLeaveOrder = async (
  matrixClient: MatrixClient,
  rootSpaceId: string,
  includeSubWorkspaceRooms: boolean
) => {
  const visited = new Set<string>();
  const order: string[] = [];

  const depthFirstSearch = async (roomId: string) => {
    if (visited.has(roomId)) return;
    visited.add(roomId);

    const shouldSkipSubWorkspaceRooms = rootSpaceId !== roomId && !includeSubWorkspaceRooms;
    if (shouldSkipLeaving(matrixClient, roomId, shouldSkipSubWorkspaceRooms)) return;

    const room = matrixClient.getRoom(roomId);

    if (room?.getType() === RoomType.Space) {
      const { breakoutRoomChildEvents, chatChildEvents, childEvents } = await getSpaceChildEvents(matrixClient, room);
      const allChildEvents = [...breakoutRoomChildEvents, ...chatChildEvents, ...childEvents];

      for (const evt of allChildEvents) {
        const childId = evt.getStateKey();
        if (!childId) continue;

        await depthFirstSearch(childId);
      }
    }

    order.push(roomId);
  };

  await depthFirstSearch(rootSpaceId);

  return order;
};
